import React from "react";
import PropTypes from "prop-types";
import { Box, Card, CardContent, CircularProgress, Grid, Typography } from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) =>
  createStyles({
    card: {
      borderRadius: 8,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      overflow: "hidden",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      border: "1px solid #e5e7eb",
    },
    content: {
      padding: theme.spacing(2),
      "&:last-child": {
        paddingBottom: theme.spacing(2),
      },
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
    },
    title: {
      fontSize: 14,
      fontWeight: 700,
      color: "#1f2937",
    },
    subtitle: {
      fontSize: 12,
      color: "#6b7280",
      whiteSpace: "nowrap",
    },
    levelRow: {
      alignItems: "stretch",
    },
    levelItem: {
      padding: theme.spacing(0, 2),
      borderRight: "1px solid #e5e7eb",
      "&:last-child": {
        borderRight: "none",
      },
      [theme.breakpoints.down("sm")]: {
        borderRight: "none",
        borderBottom: "1px solid #e5e7eb",
        "&:last-child": {
          borderBottom: "none",
        },
      },
    },
    levelLabelRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(0.75),
      minHeight: 22,
    },
    levelLabel: {
      fontSize: 13,
      fontWeight: 700,
      color: "#374151",
      lineHeight: 1.2,
    },
    levelCount: {
      fontSize: 12,
      fontWeight: 700,
      color: "#111827",
      whiteSpace: "nowrap",
    },
    track: {
      height: 8,
      borderRadius: 999,
      background: "#f3f4f6",
      overflow: "hidden",
    },
    fill: {
      height: "100%",
      borderRadius: 999,
    },
    pct: {
      marginTop: theme.spacing(0.5),
      fontSize: 11,
      color: "#9ca3af",
    },
    loadingWrap: {
      minHeight: 90,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  })
);

const PlotTrustScore = ({ levels, title, subtitle, loading }) => {
  const classes = useStyles();
  const maxPct = Math.max(...levels.map((level) => level.pct), 1);

  return (
    <Card className={classes.card}>
      <CardContent className={classes.content}>
        <div className={classes.header}>
          <Typography className={classes.title}>{title}</Typography>
          <Typography className={classes.subtitle}>{subtitle}</Typography>
        </div>
        {loading ? (
          <Box className={classes.loadingWrap}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Grid container spacing={0} className={classes.levelRow}>
            {levels.map((level) => (
              <Grid item xs={12} sm={6} md={3} key={level.label}>
                <div className={classes.levelItem}>
                  <div className={classes.levelLabelRow}>
                    <Typography className={classes.levelLabel}>{level.label}</Typography>
                    <Typography className={classes.levelCount}>{level.count.toLocaleString("ja-JP")}件</Typography>
                  </div>
                  <div className={classes.track}>
                    <div
                      className={classes.fill}
                      style={{
                        width: `${(level.pct / maxPct) * 100}%`,
                        background: level.color,
                      }}
                    />
                  </div>
                  <div className={classes.pct}>{level.pct}%</div>
                </div>
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

PlotTrustScore.propTypes = {
  levels: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
      pct: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
    })
  ).isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  loading: PropTypes.bool,
};

PlotTrustScore.defaultProps = {
  title: "信頼度スコア分布",
  subtitle: "",
  loading: false,
};

export default PlotTrustScore;
